import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Divider,
  Flex,
  Form,
  Input,
  notification,
  Select,
  Space,
  Tabs,
  Typography,
  Upload,
} from 'antd';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import type { ModelBranch, ModelBranchInstructions } from '@app/api/Branches';
import {
  useGetBranchInstructionsQuery,
  useLazyGetBranchQuery,
  useSetBranchInstructionsMutation,
  useUpdateBranchMutation,
} from '@app/api/Branches';
import { useGetChainsQuery } from '@app/api/Chains';
import { useLazyGetCountriesQuery } from '@app/api/Dictionaries';
import {
  useLazyGetLegalEntitiesForSystemQuery,
  useUpdateLegalEntityMutation,
} from '@app/api/LegalEntities';
import { timezonesApi } from '@app/api/Timezones';
import type { ModelTimezone } from '@app/api/Timezones';
import {
  EditPageButtons,
  Error403Unauthorized,
  FastCreationModal,
  OperatingHours,
} from '@app/components';
import {
  ADD_BRANCH,
  ADD_LEGAL_ENTITY_LABEL,
  BRANCHES,
  OFF,
  ONE_TIME_CODE,
} from '@app/constants';
import {
  RIGHTS_BRANCHES,
  RIGHTS_IS_CREATE,
  RIGHTS_IS_EDIT,
  RIGHTS_LEGAL_ENTITIES,
} from '@app/constants/accessRights';
import {
  ERROR,
  ERROR_REQUIRED_FIELD,
  FRONT_WARNING,
  SUCCESS,
  USER_PRINT_ERROR,
  WARNING,
} from '@app/constants/alerts';
import { PREFIX } from '@app/constants/appPrefix';
import { navigation } from '@app/data';
import {
  useAccessRights,
  useMemoizeObject,
  useNewUserAccessRights,
  useUserPrint,
} from '@app/hooks';
import { getSelectorOptions } from '@app/pages/Branches/BranchesList/utils/getSelectorOptions';
import { LeavePageContext, NotificationContext } from '@app/providers';
import type { NNotificationProvider } from '@app/providers/NotificationProvider/NotificationProvider.types';
import { handleCatchError } from '@app/services';
import { useGetWarehousesQuery } from '@app/services/rtkQuery/Warehouses/api';
import { useErrorHandler } from '@app/shared/hooks';
import { generateSpanWithDataTestId } from '@app/shared/utils';
import { setActiveBranchName, useAppDispatch } from '@app/store';
import type { NAuth, NNavigation, Options, NFiles } from '@app/types';
import type { NBranches } from '@app/types/Branches';
import type { NDictionaries } from '@app/types/Dictionaries';
import type { NLegalEntities } from '@app/types/LegalEntities';
import type { NUtils } from '@app/types/Utils';
import {
  convertDateFromSelectToISOFormat,
  generateTimes,
  getAccessToken,
} from '@app/utils';

import type { NAddBranch } from '@pages/Branches/AddBranch/AddBranch.types';
import { AdditionalTabSavingOverlay } from '@pages/Branches/AddBranch/ui/AdditionalTabSavingOverlay';
import { adaptBranchToRedux } from '@pages/Branches/AddBranch/utils/adaptBranchToRedux';
import type { ItemProps } from '@pages/Branches/BranchesList/data/StatusOptions';

import { BranchMedicalExtension } from './components';

import type { UploadFile } from 'antd';
import type { UploadRequestOption as RcUploadRequestOption } from 'rc-upload/lib/interface';

import '@pages/Branches/AddBranch/styles/AddBranch.desktop.scss';
import '@pages/Branches/AddBranch/styles/AddBranch.tablet.scss';

type InstructionFileDraft = {
  uid: string;
  file: NFiles.ModelFile;
};

const { TextArea } = Input;
const time = generateTimes().map((time, index) => ({
  value: index,
  label: time,
}));

export const AddBranch = () => {
  const [isAdditionalTabSaving, setIsAdditionalTabSaving] = useState(false);
  const accessToken = getAccessToken();
  const { hasUserRight: hasUserRightToCrete } = useAccessRights(
    RIGHTS_BRANCHES,
    RIGHTS_IS_CREATE
  );
  const { hasUserRight: hasUserRightToEdit } = useAccessRights(
    RIGHTS_BRANCHES,
    RIGHTS_IS_EDIT
  );
  const { hasUserRight: hasUserRightToCreateLegalEntity } = useAccessRights(
    RIGHTS_LEGAL_ENTITIES,
    RIGHTS_IS_EDIT
  );
  const {
    params: { ActiveBranchId, UserId, Database },
  } = useUserPrint();
  const [serverError, setServerError] = useState('');
  const [, setErrorType] = useState<NUtils.ErrorTypes>(undefined);

  const [countries, setCountries] = useState<NDictionaries.ModelCountry[]>([]);
  const [countriesOptions, setCountriesOptions] = useState<ItemProps[]>([
    {
      label: 'Россия',
      value: '1',
    },
  ]);

  const [timezones, setTimezones] = useState<ModelTimezone[]>([]);
  const [timezonesOptions, setTimezonesOptions] = useState<ItemProps[]>([]);

  const [chainsOptions, setChainsOptions] = useState<ItemProps[]>([
    {
      label: generateSpanWithDataTestId('Вне сети', 'network-item'),
      value: '0',
    },
  ]);

  const [fileList, setFileList] = useState<UploadFile<string>[]>([]);
  const [instructionFileDrafts, setInstructionFileDrafts] = useState<
    InstructionFileDraft[]
  >([]);

  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const editableBranchId = Number(searchParams.get('id'));
  const [editableBranch, setEditableBranch] = useState<NBranches.Branch>();
  const [legalEntities, setLegalEntities] = useState<
    NLegalEntities.ModelItemEx[]
  >([]);

  const {
    data: warehouses,
    isLoading: isLoadingWarehouses,
    isUninitialized: isUninitializedWarehouses,
    isFetching: isFetchingWarehouses,
    isError: isErrorWarehouses,
    error: errorWarehouses,
  } = useGetWarehousesQuery({ ActiveBranchId }, { skip: !ActiveBranchId });
  const [updateBranch] = useUpdateBranchMutation();

  useErrorHandler([{ flag: isErrorWarehouses, error: errorWarehouses }]);

  const { userAccessRights, organizationAccessRights } =
    useNewUserAccessRights();

  const { Warehouse } = userAccessRights;
  const warehouseModuleAllowed =
    organizationAccessRights.find(({ RightType }) => RightType === 9)
      ?.Allowed || false;

  const showSelectWarehouse = Warehouse.IsOn && warehouseModuleAllowed;

  const warehousesOptions: Options = useMemo(
    () =>
      warehouses?.map(({ Name, Id }) => ({
        label: Name,
        value: Id,
      })) || [],
    [warehouses]
  );

  const [mondayWorkStatus, setMondayWorkStatus] = useState(false);
  const [tuesdayWorkStatus, setTuesdayWorkStatus] = useState(false);
  const [wednesdayWorkStatus, setWednesdayWorkStatus] = useState(false);
  const [thursdayWorkStatus, setThursdayWorkStatus] = useState(false);
  const [fridayWorkStatus, setFridayWorkStatus] = useState(false);
  const [saturdayWorkStatus, setSaturdayWorkStatus] = useState(false);
  const [sundayWorkStatus, setSundayWorkStatus] = useState(false);
  const [isFormButtonsLoading, setIsFormButtonsLoading] = useState(false);
  const [isLegalEntityLoading, setIsLegalEntityLoading] = useState(false);
  const [isFastCreationModalOpen, setIsFastCreationModalOpen] = useState(false);

  const [fastCreationModalForm] = Form.useForm();

  const idRef = useRef<HTMLHeadingElement>(null);

  const { showNotification } = useContext(NotificationContext);

  const {
    initialData,
    newData,
    setNewData,
    prepareStartedData,
    checkIsDataUpdated,
    setInitialData,
    setInputData,
    setSelectorData,
  } = useContext(LeavePageContext);

  const dispatch = useAppDispatch();

  const [form] = Form.useForm();
  const [additionalTabForm] = Form.useForm();

  const [, contextHolder] = notification.useNotification();

  // Свежий список при каждом открытии: сеть, созданная только что, должна
  // сразу попасть в выпадающий список.
  const { data: fetchedChains } = useGetChainsQuery(
    { ActiveBranchId, isActive: true },
    { refetchOnMountOrArgChange: true }
  );

  const { data: branchInstruction, refetch: refetchBranchInstruction } =
    useGetBranchInstructionsQuery(
      {
        ActiveBranchId: ActiveBranchId,
        forBranchId: editableBranchId,
      },
      { skip: !ActiveBranchId || !editableBranchId }
    );
  const [setBranchInstructions] = useSetBranchInstructionsMutation();
  const [getBranch] = useLazyGetBranchQuery();
  const [getCountries] = useLazyGetCountriesQuery();
  const [getLegalEntitiesForSystem] = useLazyGetLegalEntitiesForSystemQuery();
  const [updateLegalEntity] = useUpdateLegalEntityMutation();

  const memoizedLegalEntities = useMemoizeObject(legalEntities);
  const memoizedInitialData = useMemoizeObject(initialData);
  const memoizedTimezones = useMemoizeObject(timezones);
  const memoizedEditableBranch = useMemoizeObject(editableBranch);
  const memoizedCountries = useMemoizeObject(countries);
  const memoizedFetchedChains = useMemoizeObject(fetchedChains);

  const editableBranchIdForFile = useMemo(
    () => editableBranchId || 0,
    [editableBranchId]
  );

  const findTime = useCallback(
    (editableBranchWithKey: NBranches.DayWithPostfix | undefined) => {
      if (!editableBranchWithKey) return null;

      return time.find(
        ({ label }) => label === convertISOToHHMM(editableBranchWithKey)
      )?.value;
    },
    []
  );

  useEffect(() => {
    if (editableBranchId) {
      refetchBranchInstruction();
    }
  }, [refetchBranchInstruction, editableBranchId]);

  useEffect(() => {
    idRef.current?.scrollIntoView({ behavior: 'instant', block: 'start' });
  }, []);

  useEffect(() => {
    if (!ActiveBranchId) {
      showNotification('Идентификатор филиала не найден', FRONT_WARNING);
      return;
    }

    if (!memoizedEditableBranch) return;

    const serverFiles: NFiles.ModelFile[] =
      (branchInstruction?.InstructionsFiles?.length
        ? branchInstruction.InstructionsFiles
        : branchInstruction?.InstructionsFile
        ? [branchInstruction.InstructionsFile]
        : []) ?? [];

    const nextFileList: UploadFile<string>[] = serverFiles.map(
      (file, index) => ({
        uid: String(file.Id ?? `server-${index}`),
        name: file.Name || `file-${index + 1}`,
        status: 'done',
      })
    );

    const nextDrafts: InstructionFileDraft[] = serverFiles.map(
      (file, index) => ({
        uid: String(file.Id ?? `server-${index}`),
        file: {
          Id: file.Id ?? 0,
          Name: file.Name ?? '',
          Data: file.Data ?? null,
          Lifetime: file.Lifetime ?? null,
          Description: file.Description ?? null,
        },
      })
    );

    setFileList(nextFileList);
    setInstructionFileDrafts(nextDrafts);
  }, [
    ActiveBranchId,
    branchInstruction,
    memoizedEditableBranch,
    showNotification,
  ]);

  useEffect(() => {
    if (memoizedFetchedChains) {
      const adaptedChains = memoizedFetchedChains.Chains?.map(
        ({ Id, Name }) => ({
          label: generateSpanWithDataTestId(Name || '', 'network-item'),
          value: String(Id),
        })
      );

      if (adaptedChains) {
        setChainsOptions((prev) => [...prev, ...adaptedChains]);
      }
    }
  }, [memoizedFetchedChains]);

  useEffect(() => {
    const getData = async () => {
      if (
        UserId &&
        Database &&
        ActiveBranchId &&
        accessToken !== null &&
        memoizedEditableBranch === undefined
      ) {
        try {
          if (editableBranchId) {
            const result = await getBranch({
              ActiveBranchId,
              branchId: editableBranchId,
            }).unwrap();

            const adaptedBranch = adaptBranchToRedux(result);
            setEditableBranch(adaptedBranch);
          }

          const fetchedCountries = await getCountries({
            ActiveBranchId,
          }).unwrap();
          setCountries(fetchedCountries);

          const timezonesRequest = dispatch(
            timezonesApi.endpoints.GetTimezones.initiate(undefined, {
              forceRefetch: true,
            })
          );

          try {
            setTimezones(await timezonesRequest.unwrap());
          } finally {
            timezonesRequest.unsubscribe?.();
          }

          if (memoizedEditableBranch === undefined) return;

          const handledMemoizedEditableBranch =
            memoizedEditableBranch as NBranches.Branch;

          prepareStartedData({
            branchName: handledMemoizedEditableBranch?.name,
            organization: handledMemoizedEditableBranch?.legalEntityId,
            timezone: handledMemoizedEditableBranch?.timezone,
            country: form.getFieldValue('country')
              ? form.getFieldValue('country')
              : memoizedEditableBranch
              ? String(handledMemoizedEditableBranch?.countryId)
              : null,
            city: handledMemoizedEditableBranch?.city,
            address: handledMemoizedEditableBranch?.address,
            notificationsAddress:
              handledMemoizedEditableBranch?.notificationsAddress,
            notificationsPhone:
              handledMemoizedEditableBranch?.notificationsPhone,
            isMondayWork: Boolean(handledMemoizedEditableBranch?.moS),
            isTuesdayWork: Boolean(handledMemoizedEditableBranch?.tuS),
            isWednesdayWork: Boolean(handledMemoizedEditableBranch?.weS),
            isThursdayWork: Boolean(handledMemoizedEditableBranch?.thS),
            isFridayWork: Boolean(handledMemoizedEditableBranch?.frS),
            isSaturdayWork: Boolean(handledMemoizedEditableBranch?.saS),
            isSundayWork: Boolean(handledMemoizedEditableBranch?.suS),
            MoS: findTime(handledMemoizedEditableBranch?.moS),
            MoE: findTime(handledMemoizedEditableBranch?.moE),
            TuS: findTime(handledMemoizedEditableBranch?.tuS),
            TuE: findTime(handledMemoizedEditableBranch?.tuE),
            WeS: findTime(handledMemoizedEditableBranch?.weS),
            WeE: findTime(handledMemoizedEditableBranch?.weE),
            ThS: findTime(handledMemoizedEditableBranch?.thS),
            ThE: findTime(handledMemoizedEditableBranch?.thE),
            FrS: findTime(handledMemoizedEditableBranch?.frS),
            FrE: findTime(handledMemoizedEditableBranch?.frE),
            SaS: findTime(handledMemoizedEditableBranch?.saS),
            SaE: findTime(handledMemoizedEditableBranch?.saE),
            SuS: findTime(handledMemoizedEditableBranch?.suS),
            SuE: findTime(handledMemoizedEditableBranch?.suE),
          });
        } catch (error) {
          showNotification(handleCatchError(error));
        }
      }
    };

    getData();
  }, [
    accessToken,
    UserId,
    Database,
    ActiveBranchId,
    memoizedEditableBranch,
    editableBranchId,
    form,
    showNotification,
    prepareStartedData,
    findTime,
    dispatch,
    getBranch,
    getCountries,
  ]);

  const fetchLegalEntities = useCallback(
    async (
      activeBranchId: NAuth.UserPrint['activeBranchId'],
      includeDeleted: boolean,
      showNotification: NNotificationProvider.ShowNotificationType,
      onSuccess?: (result: NLegalEntities.ModelItemEx[]) => void
    ) => {
      try {
        const result = await getLegalEntitiesForSystem({
          ActiveBranchId: activeBranchId,
          includeDeleted,
        }).unwrap();

        if (typeof onSuccess === 'function') {
          onSuccess(result);
        }
      } catch (error) {
        showNotification(handleCatchError(error), WARNING);
      }
    },
    [getLegalEntitiesForSystem]
  );

  useEffect(() => {
    const loadLegalEntities = async () => {
      if (editableBranchId) {
        await fetchLegalEntities(
          ActiveBranchId,
          Boolean(memoizedEditableBranch),
          showNotification,
          (result) => {
            const filteredResult = result.filter(
              ({ Id, IsDeleted }) =>
                !IsDeleted ||
                (memoizedEditableBranch &&
                  memoizedEditableBranch.legalEntityId === Id)
            );
            setLegalEntities(filteredResult);
          }
        );
      } else {
        await fetchLegalEntities(
          ActiveBranchId,
          Boolean(memoizedEditableBranch),
          showNotification,
          (result) => {
            setLegalEntities(result.filter(({ IsDeleted }) => !IsDeleted));
          }
        );
      }
    };

    loadLegalEntities();
  }, [
    ActiveBranchId,
    memoizedEditableBranch,
    editableBranchId,
    showNotification,
    fetchLegalEntities,
  ]);

  useEffect(() => {
    const handledMemoizedCountries = memoizedCountries || [];
    const country = String(
      handledMemoizedCountries.find(({ Name }) => Name === 'Россия')?.Id
    );

    if (handledMemoizedCountries.length) {
      getSelectorOptions({
        flag: 'countries',
        defaultData: handledMemoizedCountries,
        setOptions: setCountriesOptions,
      });

      if (!editableBranchId) {
        form.setFieldValue('country', country);

        setInitialData({
          ...memoizedInitialData,
          country,
        });

        setNewData({
          ...memoizedInitialData,
          country,
        });
      }
    }
  }, [
    memoizedCountries,
    editableBranchId,
    form,
    memoizedInitialData,
    setInitialData,
    setNewData,
  ]);

  useEffect(() => {
    const handledMemoizedTimezones = memoizedTimezones || [];

    getSelectorOptions({
      flag: 'timezone',
      defaultData: handledMemoizedTimezones,
      setOptions: setTimezonesOptions,
    });

    if (!editableBranchId) {
      const foundTimezone = handledMemoizedTimezones.find(
        ({ Id }) => Id === 'Europe/Moscow'
      )?.Id;

      form.setFieldValue('timezone', foundTimezone);

      setInitialData({
        ...memoizedInitialData,
        foundTimezone,
      });

      setNewData({
        ...memoizedInitialData,
        foundTimezone,
      });
    }
  }, [
    editableBranchId,
    form,
    memoizedInitialData,
    memoizedTimezones,
    setInitialData,
    setNewData,
  ]);

  useEffect(() => {
    if (!editableBranchId && memoizedLegalEntities?.length === 1) {
      form.setFieldValue('organization', memoizedLegalEntities[0].Id);

      setInitialData({
        ...memoizedInitialData,
        organization: 1,
      });

      setNewData({
        ...memoizedInitialData,
        organization: 1,
      });
    }
  }, [
    editableBranchId,
    form,
    memoizedInitialData,
    memoizedLegalEntities,
    setInitialData,
    setNewData,
  ]);

  useEffect(() => {
    form.setFieldsValue({
      branchName: memoizedEditableBranch?.name,
      network: memoizedEditableBranch
        ? String(memoizedEditableBranch.chainId)
        : null,
      organization: memoizedEditableBranch?.legalEntityId,
      timezone: memoizedEditableBranch?.timezone,
      country: memoizedEditableBranch
        ? String(memoizedEditableBranch?.countryId)
        : null,
      city: memoizedEditableBranch?.city,
      address: memoizedEditableBranch?.address,
      notificationsAddress: memoizedEditableBranch?.notificationsAddress,
      notificationsPhone: memoizedEditableBranch?.notificationsPhone,
      isMondayWork: Boolean(memoizedEditableBranch?.moS),
      isTuesdayWork: Boolean(memoizedEditableBranch?.tuS),
      isWednesdayWork: Boolean(memoizedEditableBranch?.weS),
      isThursdayWork: Boolean(memoizedEditableBranch?.thS),
      isFridayWork: Boolean(memoizedEditableBranch?.frS),
      isSaturdayWork: Boolean(memoizedEditableBranch?.saS),
      isSundayWork: Boolean(memoizedEditableBranch?.suS),
      MoS: findTime(memoizedEditableBranch?.moS),
      MoE: findTime(memoizedEditableBranch?.moE),
      TuS: findTime(memoizedEditableBranch?.tuS),
      TuE: findTime(memoizedEditableBranch?.tuE),
      WeS: findTime(memoizedEditableBranch?.weS),
      WeE: findTime(memoizedEditableBranch?.weE),
      ThS: findTime(memoizedEditableBranch?.thS),
      ThE: findTime(memoizedEditableBranch?.thE),
      FrS: findTime(memoizedEditableBranch?.frS),
      FrE: findTime(memoizedEditableBranch?.frE),
      SaS: findTime(memoizedEditableBranch?.saS),
      SaE: findTime(memoizedEditableBranch?.saE),
      SuS: findTime(memoizedEditableBranch?.suS),
      SuE: findTime(memoizedEditableBranch?.suE),
    });

    additionalTabForm.setFieldsValue({
      comment: memoizedEditableBranch?.comment
        ? memoizedEditableBranch.comment.replace(/\\n/g, '\n')
        : '',
    });

    setMondayWorkStatus(Boolean(memoizedEditableBranch?.moS));
    setTuesdayWorkStatus(Boolean(memoizedEditableBranch?.tuS));
    setWednesdayWorkStatus(Boolean(memoizedEditableBranch?.weS));
    setThursdayWorkStatus(Boolean(memoizedEditableBranch?.thS));
    setFridayWorkStatus(Boolean(memoizedEditableBranch?.frS));
    setSaturdayWorkStatus(Boolean(memoizedEditableBranch?.saS));
    setSundayWorkStatus(Boolean(memoizedEditableBranch?.suS));

    prepareStartedData({
      branchName: memoizedEditableBranch?.name,
      network: memoizedEditableBranch
        ? String(memoizedEditableBranch.chainId)
        : null,
      organization: memoizedEditableBranch?.legalEntityId,
      timezone: memoizedEditableBranch?.timezone,
      country: memoizedEditableBranch
        ? String(memoizedEditableBranch?.countryId)
        : null,
      city: memoizedEditableBranch?.city,
      address: memoizedEditableBranch?.address,
      notificationsAddress: memoizedEditableBranch?.notificationsAddress,
      notificationsPhone: memoizedEditableBranch?.notificationsPhone,
      isMondayWork: Boolean(memoizedEditableBranch?.moS),
      isTuesdayWork: Boolean(memoizedEditableBranch?.tuS),
      isWednesdayWork: Boolean(memoizedEditableBranch?.weS),
      isThursdayWork: Boolean(memoizedEditableBranch?.thS),
      isFridayWork: Boolean(memoizedEditableBranch?.frS),
      isSaturdayWork: Boolean(memoizedEditableBranch?.saS),
      isSundayWork: Boolean(memoizedEditableBranch?.suS),
      MoS: findTime(memoizedEditableBranch?.moS),
      MoE: findTime(memoizedEditableBranch?.moE),
      TuS: findTime(memoizedEditableBranch?.tuS),
      TuE: findTime(memoizedEditableBranch?.tuE),
      WeS: findTime(memoizedEditableBranch?.weS),
      WeE: findTime(memoizedEditableBranch?.weE),
      ThS: findTime(memoizedEditableBranch?.thS),
      ThE: findTime(memoizedEditableBranch?.thE),
      FrS: findTime(memoizedEditableBranch?.frS),
      FrE: findTime(memoizedEditableBranch?.frE),
      SaS: findTime(memoizedEditableBranch?.saS),
      SaE: findTime(memoizedEditableBranch?.saE),
      SuS: findTime(memoizedEditableBranch?.suS),
      SuE: findTime(memoizedEditableBranch?.suE),
    });
  }, [
    memoizedEditableBranch,
    form,
    prepareStartedData,
    findTime,
    additionalTabForm,
  ]);

  const convertISOToHHMM = (
    isoString: string | null | undefined
  ): string | null => {
    if (isoString) {
      const date = new Date(isoString);
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');

      return `${hours}:${minutes}`;
    } else {
      return null;
    }
  };

  const instructionFiles = useMemo(
    () => instructionFileDrafts.map((x) => x.file),
    [instructionFileDrafts]
  );

  const sendFileToServer = useCallback(async () => {
    try {
      if (!ActiveBranchId) {
        showNotification(
          'Произошла ошибка при извлечении идентификатора текущего филиала',
          FRONT_WARNING
        );
        return;
      }

      const commentRaw = additionalTabForm.getFieldValue('comment');
      const comment =
        typeof commentRaw === 'string' && commentRaw.trim() !== ''
          ? commentRaw
          : null;

      const data: ModelBranchInstructions = {
        BranchId: editableBranchIdForFile,
        Comment: comment,
        InstructionsFile: null,
        InstructionsFiles: instructionFiles.length ? instructionFiles : null,
      };

      await setBranchInstructions({ ActiveBranchId, data }).unwrap();
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  }, [
    ActiveBranchId,
    additionalTabForm,
    editableBranchIdForFile,
    instructionFiles,
    setBranchInstructions,
    showNotification,
  ]);

  const sendMainInfoByBranch = useCallback(
    async (values: NAddBranch.ArgsToSubmitForm) => {
      const {
        branchName,
        timezone,
        network,
        country,
        city,
        address,
        organization,
        notificationsAddress,
        notificationsPhone,
      } = values;
      const { comment } = additionalTabForm.getFieldsValue();

      const handleDate = (key: keyof NAddBranch.ArgsToSubmitForm) => {
        return convertDateFromSelectToISOFormat(
          time.find(({ value }) => value === values[key])?.label
        );
      };
      const handledLegalEntity = legalEntities.find(
        (item) => item.Id === organization
      )?.Name;

      if (!handledLegalEntity)
        return showNotification('Название филиала не найдено', FRONT_WARNING);

      const generalProps = {
        LegalEntityId: organization,
        LegalEntity: handledLegalEntity,
        Name: branchName,
        Timezone: timezone,
        ChainId: +network,
        CountryId: +country,
        City: city,
        Address: address,
        NotificationsAddress: notificationsAddress,
        NotificationsPhone: notificationsPhone,
        Comment: comment ? comment.replace(/\n/g, '\\n') : null,
        MoS: handleDate('MoS'),
        MoE: handleDate('MoE'),
        TuS: handleDate('TuS'),
        TuE: handleDate('TuE'),
        WeS: handleDate('WeS'),
        WeE: handleDate('WeE'),
        ThS: handleDate('ThS'),
        ThE: handleDate('ThE'),
        FrS: handleDate('FrS'),
        FrE: handleDate('FrE'),
        SaS: handleDate('SaS'),
        SaE: handleDate('SaE'),
        SuS: handleDate('SuS'),
        SuE: handleDate('SuE'),
      };

      const modelBranch: ModelBranch = editableBranchId
        ? {
            ...generalProps,
            Id: editableBranchId,
            Name: branchName,
            IsDeleted: editableBranch?.isDeleted || false,
            WarehouseId: editableBranch?.warehouseId || null,
          }
        : {
            ...generalProps,
            Id: 0,
            IsDeleted: false,
            WarehouseId: editableBranch?.warehouseId || null,
          };

      if (UserId && Database && ActiveBranchId) {
        try {
          setIsFormButtonsLoading(true);

          const updatedBranch = await updateBranch({
            ActiveBranchId,
            data: modelBranch,
          }).unwrap();

          if (updatedBranch.Id === ActiveBranchId) {
            dispatch(setActiveBranchName(updatedBranch.Name));
          }

          const errorText = editableBranchId
            ? 'Филиал обновлен успешно'
            : 'Филиал создан успешно';

          showNotification(errorText, SUCCESS);
          navigate(
            `${
              (navigation as NNavigation.SidebarItem[]).find(
                ({ id: _id }) => _id === BRANCHES
              )?.path
            }`
          );
        } catch (error) {
          setServerError(handleCatchError(error));
          setErrorType(ERROR);
        } finally {
          setIsFormButtonsLoading(false);
        }
      }
    },
    [
      additionalTabForm,
      legalEntities,
      showNotification,
      editableBranchId,
      editableBranch?.isDeleted,
      editableBranch?.warehouseId,
      UserId,
      Database,
      ActiveBranchId,
      updateBranch,
      navigate,
      dispatch,
    ]
  );

  const allowedExtensions = useMemo(
    () =>
      new Set([
        '.png',
        '.jpg',
        '.jpeg',
        '.pdf',
        '.doc',
        '.docx',
        '.xls',
        '.xlsx',
        '.txt',
      ]),
    []
  );

  const isAllowedFileExtension = useCallback(
    (fileName: string) => {
      const lower = (fileName || '').toLowerCase().trim();
      const dotIndex = lower.lastIndexOf('.');
      if (dotIndex === -1) return false;
      const ext = lower.slice(dotIndex);
      return allowedExtensions.has(ext);
    },
    [allowedExtensions]
  );

  const sendAdditionalTabData = useCallback(async () => {
    if (isAdditionalTabSaving) return;

    setIsAdditionalTabSaving(true);

    try {
      const mainInfoTabValues = form.getFieldsValue();

      const invalidFile = fileList.find(
        (f) => !isAllowedFileExtension(f.name || '')
      );
      if (invalidFile) {
        showNotification(
          'Приложенный файл имеет недопустимое расширение',
          FRONT_WARNING
        );
        return;
      }

      const handledFileName = (fileList?.[0]?.name || '').toLowerCase();

      const isAllowedPreviewExtension =
        handledFileName.endsWith('.png') ||
        handledFileName.endsWith('.jpg') ||
        handledFileName.endsWith('.jpeg');

      if (handledFileName && !isAllowedPreviewExtension) {
        showNotification(
          'Приложенный файл имеет недопустимое расширение',
          FRONT_WARNING
        );
        return;
      }

      await sendMainInfoByBranch(mainInfoTabValues);
      await sendFileToServer();
    } finally {
      setIsAdditionalTabSaving(false);
    }
  }, [
    isAdditionalTabSaving,
    form,
    fileList,
    showNotification,
    sendFileToServer,
    sendMainInfoByBranch,
    isAllowedFileExtension,
  ]);

  const handleCancel = () => {
    checkIsDataUpdated({
      path: '/branches',
      navigate,
    });
  };

  const addLegalEntity = () => {
    setIsFastCreationModalOpen(true);
  };

  const handleCancelFastCreationModal = () => {
    fastCreationModalForm.resetFields();
    setIsFastCreationModalOpen(false);
  };

  const handleFastCreationModalOk = async () => {
    await fastCreationModalForm.validateFields();

    fastCreationModalForm.submit();
    setIsFastCreationModalOpen(false);
  };

  const createLegalEntity = async (
    values: NLegalEntities.AddLegalEntityFormProps
  ) => {
    try {
      setIsLegalEntityLoading(true);

      const { Name, TaxPayerId, Kpp, Description } = values;

      const { Id } = await updateLegalEntity({
        ActiveBranchId,
        data: {
          Id: 0,
          Name: Name || null,
          Description: Description ? Description.trim() : null,
          Kpp: Kpp ? +Kpp : null,
          TaxPayerId: TaxPayerId ? +TaxPayerId : null,
          IsDeleted: false,
          CountConstantsPayments: 0,
        },
      }).unwrap();

      await fetchLegalEntities(
        ActiveBranchId,
        Boolean(editableBranch),
        showNotification,
        (result) => {
          setLegalEntities(result.filter(({ IsDeleted }) => !IsDeleted));
        }
      );

      form.setFieldValue('organization', Id);

      showNotification('Юридическое лицо создано успешно', SUCCESS);
    } catch (error) {
      showNotification(handleCatchError(error));
    } finally {
      setIsLegalEntityLoading(false);
    }
  };

  const getBase64 = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.readAsDataURL(file);
      reader.onload = () => {
        const { result } = reader;
        if (typeof result === 'string') {
          const [, base64] = result.split(',');
          resolve(base64 || '');
          return;
        }
        reject(new Error('Ошибка преобразования файла в base64'));
      };
      reader.onerror = (error) => reject(error);
    });
  }, []);

  const handleFileUploading = useCallback(
    async (file: File & { uid?: string }) => {
      try {
        if (!ActiveBranchId) {
          showNotification(
            'Произошла ошибка при извлечении идентификатора активного филиала',
            FRONT_WARNING
          );
          return;
        }

        const uid = String(file.uid ?? `${Date.now()}-${file.name}`);
        const name = file.name || 'file';

        if (!isAllowedFileExtension(name)) {
          showNotification(
            'Приложенный файл имеет недопустимое расширение',
            FRONT_WARNING
          );
          return;
        }

        const fileInBase64 = await getBase64(file);

        const fileModel: NFiles.ModelFile = {
          Id: 0,
          Name: name,
          Data: fileInBase64,
          Lifetime: null,
          Description: null,
        };

        setInstructionFileDrafts((prev) => {
          const withoutSame = prev.filter((x) => x.uid !== uid);
          return [...withoutSame, { uid, file: fileModel }];
        });

        setFileList((prev) => {
          const withoutSame = prev.filter((x) => x.uid !== uid);
          return [...withoutSame, { uid, name, status: 'done' }];
        });
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    },
    [ActiveBranchId, getBase64, isAllowedFileExtension, showNotification]
  );

  const handleRemoveFile = useCallback(
    (file: UploadFile) => {
      if (isAdditionalTabSaving) return false;

      setFileList((prev) => prev.filter((x) => x.uid !== file.uid));
      setInstructionFileDrafts((prev) =>
        prev.filter((x) => x.uid !== file.uid)
      );
      return false;
    },
    [isAdditionalTabSaving]
  );

  if (!editableBranchId && !hasUserRightToCrete) {
    return <Error403Unauthorized />;
  } else if (editableBranchId && !hasUserRightToEdit) {
    return <Error403Unauthorized />;
  }

  if (!UserId || !Database || !ActiveBranchId) {
    return <div>{USER_PRINT_ERROR}</div>;
  }

  const firstTabContent = (
    <div className={`${PREFIX}${ADD_BRANCH}`}>
      {contextHolder}
      <div className="add-branch-wrapper">
        <Form
          form={form}
          onFinish={sendMainInfoByBranch}
          style={{ maxWidth: 800, width: '100%' }}
          autoComplete={OFF}
        >
          <Space direction="vertical" size="large" className="form-space">
            <div className="container">
              <Typography.Text
                className="label-require"
                strong
                data-testid="name-branch"
              >
                Название
              </Typography.Text>

              <Form.Item
                name="branchName"
                rules={[
                  {
                    required: true,
                    message: generateSpanWithDataTestId(
                      ERROR_REQUIRED_FIELD,
                      'alert'
                    ),
                  },
                ]}
                className="mg-0"
              >
                <Input
                  data-testid="name-branch-input"
                  autoComplete={ONE_TIME_CODE}
                  maxLength={300}
                  size="large"
                  onChange={(e) => {
                    setInputData({
                      event: e,
                      fieldName: 'branchName',
                      isEditPage: Boolean(editableBranchId),
                      isTextField: true,
                    });
                  }}
                />
              </Form.Item>
            </div>

            <div className="container">
              <Typography.Text
                className="label-require"
                strong
                data-testid="network"
              >
                Сеть
              </Typography.Text>

              <Form.Item
                name="network"
                rules={[
                  {
                    required: true,
                    message: generateSpanWithDataTestId(
                      ERROR_REQUIRED_FIELD,
                      'alert'
                    ),
                  },
                ]}
                className="mg-0"
              >
                <Select
                  data-testid="network-select"
                  options={chainsOptions}
                  size="large"
                  onChange={(value) => {
                    setSelectorData({ value, fieldName: 'network' });
                  }}
                />
              </Form.Item>
            </div>

            <div className="container">
              <Typography.Text
                className="label-require"
                strong
                data-testid="legal-entity"
              >
                Юридическое лицо
              </Typography.Text>

              <Form.Item
                name="organization"
                className="mg-0"
                rules={[
                  {
                    required: true,
                    message: generateSpanWithDataTestId(
                      ERROR_REQUIRED_FIELD,
                      'alert'
                    ),
                  },
                ]}
              >
                <Select
                  data-testid="legal-entity-select"
                  size="large"
                  loading={isLegalEntityLoading}
                  options={legalEntities.map((item) => {
                    return {
                      value: item.Id,
                      label: generateSpanWithDataTestId(
                        item.Name || '',
                        'legal-entity-item'
                      ),
                    };
                  })}
                  popupRender={(menu) => (
                    <>
                      {hasUserRightToCreateLegalEntity ? (
                        <>
                          <Button
                            type="link"
                            size="small"
                            onClick={addLegalEntity}
                            data-testid="add-legal-entity"
                          >
                            {ADD_LEGAL_ENTITY_LABEL}
                          </Button>
                          <Divider style={{ margin: '8px 0' }} />
                          {menu}
                        </>
                      ) : (
                        <>{menu}</>
                      )}
                    </>
                  )}
                  onChange={(value) => {
                    setSelectorData({
                      value,
                      fieldName: 'organization',
                    });
                  }}
                />
              </Form.Item>
            </div>

            <div className="container">
              <Typography.Text
                className="label-require"
                strong
                data-testid="country"
              >
                Страна
              </Typography.Text>

              <Form.Item
                name="country"
                rules={[
                  {
                    required: true,
                    message: generateSpanWithDataTestId(
                      ERROR_REQUIRED_FIELD,
                      'alert'
                    ),
                  },
                ]}
                className="mg-0"
              >
                <Select
                  data-testid="country-select"
                  options={countriesOptions
                    .filter((country) => country.value !== '1')
                    .map(({ label, value }) => {
                      return {
                        label: generateSpanWithDataTestId(
                          label,
                          'country-item'
                        ),
                        value,
                      };
                    })}
                  size="large"
                  onChange={(value) => {
                    setSelectorData({ value, fieldName: 'country' });
                  }}
                />
              </Form.Item>
            </div>

            <div className="container">
              <Typography.Text
                className="label-require"
                strong
                data-testid="city"
              >
                Город
              </Typography.Text>

              <Form.Item
                name="city"
                rules={[
                  {
                    required: true,
                    message: generateSpanWithDataTestId(
                      ERROR_REQUIRED_FIELD,
                      'alert'
                    ),
                  },
                ]}
                className="mg-0"
              >
                <Input
                  data-testid="city-input"
                  autoComplete={ONE_TIME_CODE}
                  maxLength={80}
                  size="large"
                  onChange={(e) => {
                    setInputData({
                      event: e,
                      fieldName: 'city',
                      isEditPage: Boolean(editableBranchId),
                      isTextField: true,
                    });
                  }}
                />
              </Form.Item>
            </div>

            <div className="container">
              <Typography.Text
                className="label-require"
                strong
                data-testid="address"
              >
                Адрес
              </Typography.Text>

              <Form.Item
                name="address"
                rules={[
                  {
                    required: true,
                    message: generateSpanWithDataTestId(
                      ERROR_REQUIRED_FIELD,
                      'alert'
                    ),
                  },
                ]}
                className="mg-0"
              >
                <Input
                  data-testid="address-input"
                  autoComplete={ONE_TIME_CODE}
                  maxLength={300}
                  placeholder="Адрес (улица, дом)"
                  size="large"
                  onChange={(e) => {
                    setInputData({
                      event: e,
                      fieldName: 'address',
                      isEditPage: Boolean(editableBranchId),
                      isTextField: true,
                    });
                  }}
                />
              </Form.Item>
            </div>

            <div className="container">
              <Typography.Text
                className="label-require"
                strong
                data-testid="time-zone"
              >
                Временная зона
              </Typography.Text>

              <Form.Item
                name="timezone"
                rules={[
                  {
                    required: true,
                    message: generateSpanWithDataTestId(
                      ERROR_REQUIRED_FIELD,
                      'alert'
                    ),
                  },
                ]}
                className="mg-0"
              >
                <Select
                  data-testid={
                    editableBranchId ? 'blocked-timezone' : 'time-zone-select'
                  }
                  disabled={Boolean(editableBranchId)}
                  options={timezonesOptions.map(({ label, value }) => {
                    return {
                      label: generateSpanWithDataTestId(
                        label,
                        'time-zone-item'
                      ),
                      value,
                    };
                  })}
                  size="large"
                  onChange={(value) => {
                    setSelectorData({ value, fieldName: 'timezone' });
                  }}
                />
              </Form.Item>
            </div>

            <div className="container">
              <Typography.Text strong data-testid="address-for-mailing">
                Адрес для рассылок
              </Typography.Text>

              <Form.Item name="notificationsAddress" className="mg-0">
                <Input
                  data-testid="address-for-mailing-input"
                  autoComplete={ONE_TIME_CODE}
                  maxLength={400}
                  size="large"
                  onChange={(e) => {
                    setInputData({
                      event: e,
                      fieldName: 'notificationAddress',
                      isEditPage: Boolean(editableBranchId),
                      isTextField: true,
                    });
                  }}
                />
              </Form.Item>
            </div>

            <div className="container">
              <Typography.Text strong data-testid="phone">
                Телефон салона
              </Typography.Text>

              <Form.Item name="notificationsPhone" className="mg-0">
                <Input
                  data-testid="phone-input"
                  autoComplete={ONE_TIME_CODE}
                  maxLength={20}
                  size="large"
                  onChange={(e) => {
                    setInputData({
                      event: e,
                      fieldName: 'notificationPhone',
                      isEditPage: Boolean(editableBranchId),
                      isTextField: true,
                    });
                  }}
                />
              </Form.Item>
            </div>

            <div className="operating-hours-container">
              <Divider style={{ margin: '8px 0' }} />

              <Typography.Title
                level={3}
                className="mg-0"
                data-testid="work-mode"
              >
                Режим работы
              </Typography.Title>

              <Space
                size="middle"
                className="operating-hours-content"
                direction="vertical"
              >
                <OperatingHours
                  form={form}
                  setWorkStatus={setMondayWorkStatus}
                  dayWorkStatus={mondayWorkStatus}
                  times={time}
                  day={generateSpanWithDataTestId('Понедельник', 'monday')}
                  switchName="isMondayWork"
                  workDayStartCode="MoS"
                  workDayEndCode="MoE"
                  setNewData={setNewData}
                  newData={newData}
                />

                <OperatingHours
                  form={form}
                  setWorkStatus={setTuesdayWorkStatus}
                  dayWorkStatus={tuesdayWorkStatus}
                  times={time}
                  day={generateSpanWithDataTestId('Вторник', 'tuesday')}
                  switchName="isTuesdayWork"
                  workDayStartCode="TuS"
                  workDayEndCode="TuE"
                  setNewData={setNewData}
                  newData={newData}
                />

                <OperatingHours
                  form={form}
                  setWorkStatus={setWednesdayWorkStatus}
                  dayWorkStatus={wednesdayWorkStatus}
                  times={time}
                  day={generateSpanWithDataTestId('Среда', 'wednesday')}
                  switchName="isWednesdayWork"
                  workDayStartCode="WeS"
                  workDayEndCode="WeE"
                  setNewData={setNewData}
                  newData={newData}
                />

                <OperatingHours
                  form={form}
                  setWorkStatus={setThursdayWorkStatus}
                  dayWorkStatus={thursdayWorkStatus}
                  times={time}
                  day={generateSpanWithDataTestId('Четверг', 'thursday')}
                  switchName="isThursdayWork"
                  workDayStartCode="ThS"
                  workDayEndCode="ThE"
                  setNewData={setNewData}
                  newData={newData}
                />

                <OperatingHours
                  form={form}
                  setWorkStatus={setFridayWorkStatus}
                  dayWorkStatus={fridayWorkStatus}
                  times={time}
                  day={generateSpanWithDataTestId('Пятница', 'friday')}
                  switchName="isFridayWork"
                  workDayStartCode="FrS"
                  workDayEndCode="FrE"
                  setNewData={setNewData}
                  newData={newData}
                />

                <OperatingHours
                  form={form}
                  setWorkStatus={setSaturdayWorkStatus}
                  dayWorkStatus={saturdayWorkStatus}
                  times={time}
                  day={generateSpanWithDataTestId('Суббота', 'saturday')}
                  switchName="isSaturdayWork"
                  workDayStartCode="SaS"
                  workDayEndCode="SaE"
                  setNewData={setNewData}
                  newData={newData}
                />

                <OperatingHours
                  form={form}
                  setWorkStatus={setSundayWorkStatus}
                  dayWorkStatus={sundayWorkStatus}
                  times={time}
                  day={generateSpanWithDataTestId('Воскресенье', 'sunday')}
                  switchName="isSundayWork"
                  workDayStartCode="SuS"
                  workDayEndCode="SuE"
                  setNewData={setNewData}
                  newData={newData}
                />
              </Space>
            </div>

            <EditPageButtons
              serverError={serverError}
              errorType={ERROR}
              setServerError={setServerError}
              isLoading={isFormButtonsLoading}
              handleCancel={handleCancel}
              isEditPage={false}
            />
          </Space>
        </Form>

        {editableBranchId ? (
          <Typography.Text
            className="id-label"
            ref={idRef}
            data-testid="branch-id"
          >
            ID: {editableBranchId}
          </Typography.Text>
        ) : (
          ''
        )}
      </div>
      <FastCreationModal
        titleDataTestId="add-legal-entity-title"
        title="Создание юридического лица"
        isModalOpen={isFastCreationModalOpen}
        handleOk={handleFastCreationModalOk}
        handleCancel={handleCancelFastCreationModal}
        modalForm={fastCreationModalForm}
        finishModal={createLegalEntity}
        nameMaxLength={40}
        descriptionMaxLength={260}
        isFastCreateLegalEntityModal={true}
      />
    </div>
  );

  const secondTabContent = (
    <div className={`${PREFIX}branch-additional-tab`}>
      <div className={`${PREFIX}branch-additional-tab__content`}>
        {isAdditionalTabSaving && (
          <AdditionalTabSavingOverlay tip="Сохранение дополнительной информации…" />
        )}

        <Form
          form={additionalTabForm}
          onFinish={sendAdditionalTabData}
          className="additional-tab-form"
          autoComplete={OFF}
        >
          <Flex justify="space-between">
            <Typography.Text
              className="comment-label"
              strong
              data-testid="note"
            >
              Примечание
            </Typography.Text>

            <Space className="comment-box" direction="vertical">
              <Form.Item name="comment" className="mg-0">
                <TextArea
                  size="large"
                  maxLength={5000}
                  data-testid="note-input"
                  disabled={isAdditionalTabSaving}
                />
              </Form.Item>

              <Form.Item name="instructionFileThumbnail" className="mg-0">
                <Upload
                  multiple
                  maxCount={10}
                  accept=".png,.jpg,.jpeg,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                  fileList={fileList}
                  disabled={isAdditionalTabSaving}
                  customRequest={async (options: RcUploadRequestOption) => {
                    const { file, onSuccess, onError } = options;
                    try {
                      if (isAdditionalTabSaving) return;

                      const realFile = file as File & { uid?: string };
                      await handleFileUploading(realFile);
                      onSuccess?.(null as unknown as string);
                    } catch (error) {
                      onError?.(error as Error);
                    }
                  }}
                  onRemove={handleRemoveFile}
                  itemRender={(originNode) => (
                    <div data-testid="attached-file-name">{originNode}</div>
                  )}
                  showUploadList={{
                    showPreviewIcon: false,
                    showRemoveIcon: true,
                    removeIcon: (file) => (
                      <DeleteOutlined
                        data-testid={`delete-file-icon-${file.uid}`}
                      />
                    ),
                  }}
                >
                  <Button
                    block
                    type="primary"
                    icon={<PlusOutlined />}
                    data-testid="attach-file"
                    disabled={isAdditionalTabSaving}
                  >
                    Выбрать файлы
                  </Button>
                </Upload>
              </Form.Item>
            </Space>
          </Flex>

          {showSelectWarehouse && (
            <Flex justify="space-between" align="center">
              <Typography.Text
                className="comment-label"
                strong
                data-testid="note"
              >
                Основной склад
              </Typography.Text>

              <Space className="comment-box" direction="vertical">
                <Select<number>
                  className="full-width"
                  value={editableBranch?.warehouseId}
                  options={warehousesOptions}
                  disabled={
                    isAdditionalTabSaving ||
                    isFetchingWarehouses ||
                    isLoadingWarehouses ||
                    isUninitializedWarehouses
                  }
                  onChange={(value) => {
                    setEditableBranch((prev) =>
                      prev ? { ...prev, warehouseId: value } : prev
                    );
                  }}
                />
              </Space>
            </Flex>
          )}

          <Button
            type="primary"
            htmlType="submit"
            className="action-button"
            size="large"
            data-testid="save"
            disabled={isAdditionalTabSaving}
            loading={isAdditionalTabSaving}
          >
            Сохранить
          </Button>
        </Form>
      </div>
    </div>
  );

  if (editableBranchId) {
    return (
      <Tabs
        className={`${PREFIX}global-tab-styles`}
        tabPosition="left"
        items={[
          {
            label: generateSpanWithDataTestId(
              'Общая информация',
              'general-info'
            ),
            key: '1',
            children: firstTabContent,
          },
          {
            label: generateSpanWithDataTestId(
              'Дополнительно',
              'additional-info'
            ),
            key: '2',
            forceRender: true,
            children: secondTabContent,
          },
          {
            label: 'Мед. расширение филиала',
            key: '3',
            children: <BranchMedicalExtension branchId={editableBranchId} />,
          },
        ]}
      />
    );
  }

  return firstTabContent;
};
